(() => {
  if (!customElements.get('saphir-action-gallery')) {
    customElements.define('saphir-action-gallery', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        this.list = this.querySelector('.saphir-action__cards');
        this.navigation = this.querySelector('.saphir-action__navigation');
        if (!this.list || !this.navigation) return;
        const options = { signal: this.abort.signal };
        this.addEventListener('click', event => {
          const link = event.target.closest('a.saphir-action__product[data-current-product]');
          if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const picker = Array.from(document.querySelectorAll('main .product-info variant-picker'))
            .find(element => element.getAttribute('handle') === link.dataset.productHandle);
          const variantId = Number(link.dataset.variantId);
          const variant = picker?.product?.variants?.find(item => item.id === variantId);
          // Retain the normal same-tab URL when the theme picker cannot select this variant.
          if (!variant || typeof picker.selectVariant !== 'function') return;
          const hideSoldOut = picker.hasAttribute('hide-sold-out-variants');
          try {
            // Preserve the referenced colour even when this size is sold out.
            if (hideSoldOut) picker.removeAttribute('hide-sold-out-variants');
            picker.selectVariant(variantId);
            if (picker.selectedVariant?.id !== variantId) return;
          } catch (_) {
            return;
          } finally {
            if (hideSoldOut) picker.setAttribute('hide-sold-out-variants', '');
          }
          event.preventDefault();
          this.querySelectorAll('video').forEach(video => video.pause());
          const heading = picker.closest('.product')?.querySelector('h1');
          if (heading) {
            const originalTabindex = heading.getAttribute('tabindex');
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
            heading.addEventListener('blur', () => {
              if (originalTabindex === null) heading.removeAttribute('tabindex');
              else heading.setAttribute('tabindex', originalTabindex);
            }, { once: true });
          }
          window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }, options);
        this.navigation.querySelector('[data-total]').textContent = this.list.children.length;
        this.navigation.addEventListener('click', event => {
          const button = event.target.closest('[data-direction]');
          if (!button) return;
          const cards = Array.from(this.list.children);
          if (cards.length < 2) return;
          const step = Math.abs(cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left);
          const direction = button.dataset.direction === 'next' ? 1 : -1;
          const rtl = getComputedStyle(this.list).direction === 'rtl' ? -1 : 1;
          // Move one card even when several cards are visible; the browser clamps the final step.
          this.list.scrollBy({ left: direction * rtl * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }, options);
        this.list.addEventListener('scroll', () => this.update(), { ...options, passive: true });
        this.observer = new ResizeObserver(() => this.update());
        this.observer.observe(this.list);
        this.update();
      }
      update() {
        const cards = Array.from(this.list.children);
        const bounds = this.list.getBoundingClientRect();
        const media = this.list.querySelector('.saphir-action__media');
        const carousel = this.querySelector('.saphir-action__carousel');
        if (media && carousel) carousel.style.setProperty('--action-media-midpoint', `${media.getBoundingClientRect().top - carousel.getBoundingClientRect().top + media.getBoundingClientRect().height / 2}px`);
        const visible = cards.map((card, index) => ({ rect: card.getBoundingClientRect(), index }))
          .filter(({ rect }) => rect.left >= bounds.left - 2 && rect.right <= bounds.right + 2);
        const offset = Math.abs(this.list.scrollLeft);
        const maximum = this.list.scrollWidth - this.list.clientWidth;
        this.navigation.hidden = maximum <= 2;
        if (visible.length) {
          const first = visible[0].index + 1;
          const last = visible[visible.length - 1].index + 1;
          this.navigation.querySelector('[data-current]').textContent = first === last ? first : `${first}–${last}`;
        }
        this.navigation.querySelector('[data-direction="previous"]').disabled = offset <= 2;
        this.navigation.querySelector('[data-direction="next"]').disabled = offset >= maximum - 2;
      }
      disconnectedCallback() {
        this.abort?.abort();
        this.observer?.disconnect();
      }
    });
  }
  if (!customElements.get('saphir-action-video')) {
    customElements.define('saphir-action-video', class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        this.video = this.querySelector('video');
        if (!this.video) return;
        const options = { signal: this.abort.signal };
        const playButton = this.querySelector('.saphir-action__play');
        if (playButton) {
          playButton.hidden = false;
          playButton.addEventListener('click', () => {
            this.video.play().catch(() => { this.querySelector('.saphir-action__error').hidden = false; });
          }, options);
          this.video.addEventListener('pause', () => { playButton.hidden = false; }, options);
        }
        this.video.addEventListener('play', () => {
          if (playButton) playButton.hidden = true;
          document.querySelectorAll('saphir-action-video video').forEach(video => {
            if (video !== this.video) video.pause();
          });
          this.querySelector('.saphir-action__error').hidden = true;
        }, options);
        const showError = () => { this.querySelector('.saphir-action__error').hidden = false; };
        this.video.addEventListener('error', showError, options);
        // A video with source children can emit errors on each source instead of the video.
        const sources = Array.from(this.video.querySelectorAll('source'));
        const failedSources = new Set();
        sources.forEach(source => source.addEventListener('error', () => {
          failedSources.add(source);
          if (failedSources.size === sources.length) showError();
        }, options));
      }
      disconnectedCallback() {
        this.video?.pause();
        this.abort?.abort();
      }
    });
  }
})();
